/**
 * Generuje teksturę Phaser item_<id> gdy brak PNG — czysty kształt Toca-Boca.
 * Wywołuj ensureItemTexture() przed renderem awatara / wieszaka.
 */
import Phaser from 'phaser';
import { itemTextureKey } from './parseOutfit.js';
import {
  itemVisualSpec,
  type ItemShapeKind,
  type ItemVisualSpec,
} from './itemVisual.js';

const TEX_SIZE = 128;

/**
 * Zapewnia istnienie tekstury `item_<id>`.
 * Jeśli jest już (z Preload PNG) — no-op. W przeciwnym razie rysuje proceduralnie.
 * Zwraca klucz tekstury.
 */
export function ensureItemTexture(
  scene: Phaser.Scene,
  itemId: string,
): string {
  const key = itemTextureKey(itemId);
  if (scene.textures.exists(key)) return key;

  const spec = itemVisualSpec(itemId);
  const g = scene.make.graphics({ x: 0, y: 0 });
  drawItemVisual(g, spec, TEX_SIZE, TEX_SIZE);
  g.generateTexture(key, TEX_SIZE, TEX_SIZE);
  g.destroy();
  return key;
}

/** Zapewnij tekstury dla listy id (np. cały katalog). */
export function ensureItemTextures(
  scene: Phaser.Scene,
  itemIds: readonly string[],
): void {
  for (const id of itemIds) {
    ensureItemTexture(scene, id);
  }
}

/** Rysuj spec na Graphics (środek canvasu). */
export function drawItemVisual(
  g: Phaser.GameObjects.Graphics,
  spec: ItemVisualSpec,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;
  const color = spec.color;
  const accent = spec.accent;

  // miękkie tło przezroczyste — fill alpha 0
  drawShape(g, spec.shape, cx, cy, w, h, color, accent);
  drawPattern(g, spec, cx, cy, w, h);
  // biała obwódka na wierzchu (Toca)
  g.lineStyle(3, 0xffffff, 0.95);
  outlineShape(g, spec.shape, cx, cy, w, h);
}

function drawShape(
  g: Phaser.GameObjects.Graphics,
  shape: ItemShapeKind,
  cx: number,
  cy: number,
  w: number,
  h: number,
  color: number,
  accent: number,
): void {
  g.fillStyle(color, 1);

  switch (shape) {
    case 'dress': {
      // spódnica trapez + dekolt
      g.fillTriangle(cx - 40, cy - 20, cx + 40, cy - 20, cx, cy + 48);
      g.fillStyle(accent, 1);
      g.fillTriangle(cx - 22, cy - 22, cx + 22, cy - 22, cx, cy + 10);
      g.fillStyle(color, 1);
      g.fillEllipse(cx, cy - 28, 44, 28);
      break;
    }
    case 'shirt': {
      g.fillRoundedRect(cx - 32, cy - 28, 64, 56, 10);
      g.fillTriangle(cx - 32, cy - 20, cx - 48, cy + 8, cx - 28, cy + 8);
      g.fillTriangle(cx + 32, cy - 20, cx + 48, cy + 8, cx + 28, cy + 8);
      break;
    }
    case 'coat': {
      g.fillRoundedRect(cx - 36, cy - 30, 72, 62, 12);
      g.fillStyle(accent, 1);
      g.fillRect(cx - 4, cy - 24, 8, 50);
      g.fillStyle(color, 1);
      g.fillTriangle(cx - 36, cy - 22, cx - 52, cy + 12, cx - 30, cy + 12);
      g.fillTriangle(cx + 36, cy - 22, cx + 52, cy + 12, cx + 30, cy + 12);
      break;
    }
    case 'sweater': {
      g.fillRoundedRect(cx - 34, cy - 26, 68, 54, 14);
      g.fillEllipse(cx - 40, cy - 4, 22, 28);
      g.fillEllipse(cx + 40, cy - 4, 22, 28);
      g.fillStyle(accent, 0.5);
      g.fillRect(cx - 28, cy + 8, 56, 6);
      break;
    }
    case 'hat': {
      g.fillEllipse(cx, cy + 8, 90, 22);
      g.fillEllipse(cx, cy - 12, 56, 40);
      break;
    }
    case 'crown': {
      g.fillStyle(color, 1);
      g.fillTriangle(cx - 36, cy + 16, cx - 36, cy - 8, cx - 18, cy + 16);
      g.fillTriangle(cx - 18, cy + 16, cx, cy - 28, cx + 18, cy + 16);
      g.fillTriangle(cx + 18, cy + 16, cx + 36, cy - 8, cx + 36, cy + 16);
      g.fillRect(cx - 36, cy + 8, 72, 14);
      g.fillStyle(accent, 1);
      g.fillCircle(cx, cy - 10, 6);
      break;
    }
    case 'beret': {
      g.fillEllipse(cx + 4, cy - 4, 70, 36);
      g.fillCircle(cx + 28, cy - 18, 8);
      break;
    }
    case 'winter_hat': {
      g.fillEllipse(cx, cy + 10, 70, 20);
      g.fillEllipse(cx, cy - 14, 58, 44);
      g.fillStyle(0xffffff, 1);
      g.fillCircle(cx, cy - 36, 10);
      g.fillStyle(accent, 1);
      g.fillRect(cx - 32, cy + 4, 64, 10);
      break;
    }
    case 'party_hat': {
      g.fillTriangle(cx - 28, cy + 20, cx + 28, cy + 20, cx, cy - 40);
      g.fillStyle(accent, 1);
      g.fillCircle(cx, cy - 40, 8);
      break;
    }
    case 'wizard_hat': {
      // wysoki stożek + rondo
      g.fillEllipse(cx, cy + 22, 72, 18);
      g.fillTriangle(cx - 26, cy + 18, cx + 26, cy + 18, cx - 6, cy - 48);
      g.fillStyle(accent, 1);
      g.fillCircle(cx - 4, cy - 10, 5);
      g.fillCircle(cx + 6, cy + 2, 4);
      break;
    }
    case 'baseball_cap': {
      // daszek + kopuła
      g.fillEllipse(cx, cy - 4, 56, 36);
      g.fillStyle(accent, 1);
      g.fillEllipse(cx + 18, cy + 10, 48, 16);
      g.fillStyle(color, 1);
      g.fillEllipse(cx - 4, cy - 2, 40, 28);
      break;
    }
    case 'flower_hat': {
      g.fillEllipse(cx, cy + 10, 88, 20);
      g.fillEllipse(cx, cy - 8, 52, 36);
      // kwiatek na rondo
      g.fillStyle(accent, 1);
      for (const ang of [0, 1.05, 2.1, 3.14, 4.19, 5.24]) {
        g.fillCircle(cx + 18 + Math.cos(ang) * 10, cy - 22 + Math.sin(ang) * 10, 6);
      }
      g.fillStyle(0xffe066, 1);
      g.fillCircle(cx + 18, cy - 22, 5);
      break;
    }
    case 'bow': {
      g.fillEllipse(cx - 18, cy, 36, 28);
      g.fillEllipse(cx + 18, cy, 36, 28);
      g.fillStyle(accent, 1);
      g.fillCircle(cx, cy, 12);
      break;
    }
    case 'glasses': {
      g.lineStyle(5, color, 1);
      g.strokeCircle(cx - 18, cy, 16);
      g.strokeCircle(cx + 18, cy, 16);
      g.lineBetween(cx - 2, cy, cx + 2, cy);
      g.lineBetween(cx - 34, cy - 2, cx - 42, cy - 8);
      g.lineBetween(cx + 34, cy - 2, cx + 42, cy - 8);
      break;
    }
    case 'sun_glasses': {
      g.fillStyle(color, 1);
      g.fillRoundedRect(cx - 36, cy - 12, 28, 22, 6);
      g.fillRoundedRect(cx + 8, cy - 12, 28, 22, 6);
      g.fillRect(cx - 8, cy - 4, 16, 6);
      break;
    }
    case 'heart_glasses': {
      g.fillStyle(color, 1);
      drawHeart(g, cx - 18, cy, 14);
      drawHeart(g, cx + 18, cy, 14);
      g.fillRect(cx - 6, cy - 2, 12, 5);
      break;
    }
    case 'cat_glasses': {
      // kocie oczy (spiczaste rogi)
      g.fillStyle(color, 1);
      g.fillEllipse(cx - 18, cy + 2, 30, 20);
      g.fillEllipse(cx + 18, cy + 2, 30, 20);
      g.fillTriangle(cx - 32, cy - 4, cx - 18, cy - 18, cx - 8, cy - 4);
      g.fillTriangle(cx + 8, cy - 4, cx + 18, cy - 18, cx + 32, cy - 4);
      g.fillRect(cx - 6, cy, 12, 5);
      g.fillStyle(accent, 0.85);
      g.fillEllipse(cx - 18, cy + 2, 16, 12);
      g.fillEllipse(cx + 18, cy + 2, 16, 12);
      break;
    }
    case 'star_glasses': {
      drawStar(g, cx - 18, cy, 5, 18, 8, color);
      drawStar(g, cx + 18, cy, 5, 18, 8, color);
      g.fillStyle(color, 1);
      g.fillRect(cx - 6, cy - 2, 12, 5);
      g.fillStyle(accent, 0.9);
      g.fillCircle(cx - 18, cy, 5);
      g.fillCircle(cx + 18, cy, 5);
      break;
    }
    case 'collar': {
      // Pasek obroży (gruby stroke elipsy) + breloczek — wygląda jak na szyi, nie plama.
      g.lineStyle(12, color, 1);
      g.strokeEllipse(cx, cy - 2, 78, 30);
      g.lineStyle(3, 0xffffff, 0.9);
      g.strokeEllipse(cx, cy - 2, 78, 30);
      // klamra
      g.fillStyle(0xfff8f0, 1);
      g.fillRoundedRect(cx - 10, cy - 10, 20, 14, 3);
      g.lineStyle(2, accent, 1);
      g.strokeRoundedRect(cx - 10, cy - 10, 20, 14, 3);
      // charm / serduszko-dzwonek pod klamrą
      g.fillStyle(accent, 1);
      g.fillCircle(cx, cy + 14, 9);
      g.fillStyle(0xfff8f0, 0.95);
      g.fillCircle(cx, cy + 14, 4);
      break;
    }
    case 'bandana': {
      // chustka zawiązana na szyi — trójkąt + węzeł
      g.fillStyle(color, 1);
      g.fillTriangle(cx - 42, cy - 6, cx + 42, cy - 6, cx, cy + 26);
      g.fillStyle(accent, 1);
      g.fillTriangle(cx - 14, cy + 6, cx + 14, cy + 6, cx, cy + 30);
      g.fillCircle(cx, cy + 4, 6);
      break;
    }
    case 'pearl_collar': {
      // cienki pasek + rząd pereł (nie pełna elipsa)
      g.lineStyle(6, color, 1);
      g.strokeEllipse(cx, cy - 2, 80, 28);
      g.fillStyle(0xffffff, 1);
      for (let i = -3; i <= 3; i++) {
        g.fillCircle(cx + i * 11, cy + (i % 2 === 0 ? 4 : 0), 6);
        g.fillStyle(accent, 0.5);
        g.fillCircle(cx + i * 11 - 1, cy + (i % 2 === 0 ? 3 : -1), 2);
        g.fillStyle(0xffffff, 1);
      }
      g.fillStyle(accent, 1);
      g.fillCircle(cx, cy + 14, 7);
      break;
    }
    case 'shoes': {
      g.fillEllipse(cx - 22, cy + 4, 36, 22);
      g.fillEllipse(cx + 22, cy + 4, 36, 22);
      g.fillStyle(accent, 1);
      g.fillEllipse(cx - 22, cy + 8, 20, 10);
      g.fillEllipse(cx + 22, cy + 8, 20, 10);
      break;
    }
    case 'boots': {
      g.fillRoundedRect(cx - 36, cy - 16, 28, 40, 8);
      g.fillRoundedRect(cx + 8, cy - 16, 28, 40, 8);
      g.fillEllipse(cx - 22, cy + 22, 34, 16);
      g.fillEllipse(cx + 22, cy + 22, 34, 16);
      break;
    }
    case 'ballet': {
      // delikatne baletki z wstążkami
      g.fillEllipse(cx - 22, cy + 8, 32, 18);
      g.fillEllipse(cx + 22, cy + 8, 32, 18);
      g.fillStyle(accent, 1);
      g.fillEllipse(cx - 22, cy + 6, 18, 10);
      g.fillEllipse(cx + 22, cy + 6, 18, 10);
      g.lineStyle(3, accent, 0.9);
      g.lineBetween(cx - 22, cy + 2, cx - 30, cy - 16);
      g.lineBetween(cx - 22, cy + 2, cx - 12, cy - 16);
      g.lineBetween(cx + 22, cy + 2, cx + 12, cy - 16);
      g.lineBetween(cx + 22, cy + 2, cx + 30, cy - 16);
      break;
    }
    case 'bag': {
      g.fillRoundedRect(cx - 22, cy - 8, 44, 40, 10);
      g.lineStyle(4, accent, 1);
      g.strokeEllipse(cx, cy - 18, 28, 20);
      break;
    }
    case 'star': {
      drawStar(g, cx, cy, 5, 36, 16, color);
      break;
    }
    case 'backpack': {
      g.fillRoundedRect(cx - 24, cy - 20, 48, 52, 12);
      g.fillStyle(accent, 1);
      g.fillRoundedRect(cx - 16, cy - 8, 32, 24, 6);
      g.fillStyle(color, 1);
      g.fillRect(cx - 28, cy - 12, 8, 28);
      g.fillRect(cx + 20, cy - 12, 8, 28);
      break;
    }
    case 'scarf': {
      g.fillEllipse(cx, cy - 8, 70, 24);
      g.fillRoundedRect(cx + 8, cy - 4, 18, 48, 8);
      g.fillStyle(accent, 1);
      g.fillRect(cx - 24, cy - 12, 48, 6);
      break;
    }
    case 'bowtie': {
      // klasyczna muszka
      g.fillTriangle(cx - 6, cy, cx - 36, cy - 16, cx - 36, cy + 16);
      g.fillTriangle(cx + 6, cy, cx + 36, cy - 16, cx + 36, cy + 16);
      g.fillStyle(accent, 1);
      g.fillRoundedRect(cx - 10, cy - 10, 20, 20, 4);
      break;
    }
    case 'wings': {
      g.fillStyle(color, 0.95);
      g.fillEllipse(cx - 28, cy, 40, 56);
      g.fillEllipse(cx + 28, cy, 40, 56);
      g.fillStyle(0xffffff, 0.55);
      g.fillEllipse(cx - 28, cy, 22, 34);
      g.fillEllipse(cx + 28, cy, 22, 34);
      break;
    }
    case 'suit': {
      g.fillRoundedRect(cx - 30, cy - 24, 60, 56, 16);
      g.fillStyle(accent, 1);
      g.fillCircle(cx - 12, cy - 4, 5);
      g.fillCircle(cx + 12, cy - 4, 5);
      g.fillCircle(cx, cy + 12, 5);
      break;
    }
    case 'cap': {
      g.fillEllipse(cx, cy + 6, 70, 28);
      g.fillEllipse(cx, cy - 10, 58, 36);
      break;
    }
    default: {
      g.fillRoundedRect(cx - 28, cy - 28, 56, 56, 14);
      break;
    }
  }
}

function outlineShape(
  g: Phaser.GameObjects.Graphics,
  shape: ItemShapeKind,
  cx: number,
  cy: number,
  _w: number,
  _h: number,
): void {
  // lekkie obrysy kluczowych form
  switch (shape) {
    case 'hat':
      g.strokeEllipse(cx, cy + 8, 90, 22);
      g.strokeEllipse(cx, cy - 12, 56, 40);
      break;
    case 'bow':
      g.strokeEllipse(cx - 18, cy, 36, 28);
      g.strokeEllipse(cx + 18, cy, 36, 28);
      g.strokeCircle(cx, cy, 12);
      break;
    case 'dress':
      g.strokeTriangle(cx - 40, cy - 20, cx + 40, cy - 20, cx, cy + 48);
      break;
    case 'glasses':
    case 'sun_glasses':
    case 'heart_glasses':
    case 'cat_glasses':
    case 'star_glasses':
      // już narysowane liniami / wypełnieniem
      break;
    case 'wizard_hat':
      g.strokeTriangle(cx - 26, cy + 18, cx + 26, cy + 18, cx - 6, cy - 48);
      g.strokeEllipse(cx, cy + 22, 72, 18);
      break;
    case 'bowtie':
      g.strokeTriangle(cx - 6, cy, cx - 36, cy - 16, cx - 36, cy + 16);
      g.strokeTriangle(cx + 6, cy, cx + 36, cy - 16, cx + 36, cy + 16);
      break;
    case 'baseball_cap':
      g.strokeEllipse(cx, cy - 4, 56, 36);
      break;
    case 'flower_hat':
      g.strokeEllipse(cx, cy + 10, 88, 20);
      g.strokeEllipse(cx, cy - 8, 52, 36);
      break;
    default:
      g.strokeRoundedRect(cx - 30, cy - 28, 60, 56, 12);
      break;
  }
}

function drawPattern(
  g: Phaser.GameObjects.Graphics,
  spec: ItemVisualSpec,
  cx: number,
  cy: number,
  _w: number,
  _h: number,
): void {
  if (spec.pattern === 'none') return;
  if (spec.pattern === 'stripes') {
    g.lineStyle(4, 0xffffff, 0.65);
    for (let i = -2; i <= 2; i++) {
      const x = cx + i * 12;
      g.lineBetween(x, cy - 24, x, cy + 28);
    }
    return;
  }
  if (spec.pattern === 'dots') {
    g.fillStyle(0xffffff, 0.85);
    for (const [dx, dy] of [
      [-14, -8],
      [10, -6],
      [-6, 12],
      [14, 10],
      [0, 0],
    ] as const) {
      g.fillCircle(cx + dx, cy + dy, 4);
    }
    return;
  }
  if (spec.pattern === 'flowers') {
    g.fillStyle(0xffffff, 0.9);
    for (const [dx, dy] of [
      [-16, -6],
      [14, 4],
      [0, 16],
    ] as const) {
      g.fillCircle(cx + dx, cy + dy, 5);
      g.fillStyle(spec.accent, 1);
      g.fillCircle(cx + dx, cy + dy, 2);
      g.fillStyle(0xffffff, 0.9);
    }
    return;
  }
  if (spec.pattern === 'hearts') {
    g.fillStyle(spec.accent, 0.9);
    drawHeart(g, cx - 12, cy + 4, 7);
    drawHeart(g, cx + 14, cy - 6, 6);
  }
}

function drawHeart(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  r: number,
): void {
  g.fillCircle(cx - r * 0.55, cy - r * 0.2, r * 0.7);
  g.fillCircle(cx + r * 0.55, cy - r * 0.2, r * 0.7);
  g.fillTriangle(
    cx - r * 1.15,
    cy,
    cx + r * 1.15,
    cy,
    cx,
    cy + r * 1.2,
  );
}

function drawStar(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  points: number,
  outer: number,
  inner: number,
  color: number,
): void {
  g.fillStyle(color, 1);
  const step = Math.PI / points;
  const verts: { x: number; y: number }[] = [];
  for (let i = 0; i < points * 2; i++) {
    const rad = i % 2 === 0 ? outer : inner;
    const ang = -Math.PI / 2 + i * step;
    verts.push({ x: cx + Math.cos(ang) * rad, y: cy + Math.sin(ang) * rad });
  }
  // fan triangles from center
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i]!;
    const b = verts[(i + 1) % verts.length]!;
    g.fillTriangle(cx, cy, a.x, a.y, b.x, b.y);
  }
}
